import { memo, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  Cell,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  Bar,
  XAxis,
} from 'recharts';
import type { BarChartData, ChartProps } from '@/types';
import { ChartTooltipWrapper } from '../ChartTooltip';
import useChartInteraction from '@/hooks/useChartInteraction';
import { CampaignTypesTooltipContent } from './CampaignTypesTooltipContent';

const BarChart = ({
  className,
  data,
  tooltip = true,
  selectedSegment,
  setSelectedSegment,
  ariaLabel,
  onBarHover,
}: ChartProps) => {
  const { containerRef, chartData, handleClick } = useChartInteraction({
    data,
    selectedSegment,
    setSelectedSegment,
  });

  const transformedChartData: BarChartData[] = useMemo(() => {
    return (
      Object.entries(data).map(([key, value], index) => ({
        ...value,
        key,
        name: value.name,
        color: chartData[index]?.color,
        total: {
          amount: value.total.amount,
          currency: value.total.currency,
        },
        percentage: value.percentage,
      })) || []
    );
  }, [data, chartData]);

  return (
    <div className={className} ref={containerRef} role="img" aria-label={ariaLabel}>
      <ResponsiveContainer className="size-full">
        <RechartsBarChart data={transformedChartData}>
          {tooltip && (
            <Tooltip
              cursor={false}
              isAnimationActive={true}
              allowEscapeViewBox={{ x: true }}
              animationDuration={1000}
              content={({ active, payload }: TooltipProps<number, string>) => (
                <ChartTooltipWrapper className="min-w-48 border-[#F0F0F9] bg-[#FAFAFA] text-textDarkGrey">
                  {active && payload && payload[0] && (
                    <CampaignTypesTooltipContent payload={payload[0].payload} />
                  )}
                </ChartTooltipWrapper>
              )}
            />
          )}

          <Bar
            dataKey="total.amount"
            onClick={handleClick}
            minPointSize={2.5}
            data-testid="bar-chart-bar"
            onMouseEnter={onBarHover}
          >
            {transformedChartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                className={twMerge(
                  `hover:cursor-pointer fill-chartColors-color${
                    (index + 1) % (transformedChartData.length + 1)
                  }`,
                  selectedSegment !== null && selectedSegment !== entry.name && 'opacity-25'
                )}
              />
            ))}
          </Bar>
          <XAxis tickLine={false} tick={false} height={1} />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default memo(BarChart);
